import React from 'react';
import {
  Box,
  Avatar,
  Typography,
  Button,
  Paper,
  Chip,
  Tooltip,
  IconButton,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { useTranslation } from 'react-i18next';

function getInitials(firstName, lastName) {
  const f = firstName ? firstName.charAt(0).toUpperCase() : '';
  const l = lastName ? lastName.charAt(0).toUpperCase() : '';
  return f + l || '?';
}

function formatRelativeTime(isoString, t) {
  try {
    const now = Date.now();
    const then = new Date(isoString).getTime();
    const diffMs = now - then;
    const diffSec = Math.floor(diffMs / 1000);
    if (diffSec < 60) return t('time_just_now');
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return t('time_minutes_short', { count: diffMin });
    const diffHour = Math.floor(diffMin / 60);
    if (diffHour < 24) return t('time_hours_short', { count: diffHour });
    const diffDay = Math.floor(diffHour / 24);
    if (diffDay === 1) return t('time_yesterday');
    return t('time_days_ago', { count: diffDay });
  } catch {
    return '';
  }
}

function truncate(text, max) {
  if (!text) return '';
  return text.length > max ? text.slice(0, max) + '…' : text;
}

/**
 * SuggestionCard — Google Docs–style suggestion (track-changes) card.
 *
 * Collapsed (isActive=false): compact 1-line diff preview, no interactions.
 * Expanded (isActive=true): full diff + Accept/Reject buttons.
 */
const SuggestionCard = ({
  suggestion,
  isActive,
  onActivate,
  onAccept,
  onReject,
}) => {
  const { t } = useTranslation();

  if (!suggestion) return null;

  const isSystem = suggestion.is_system || false;
  const isSettled = suggestion.status === 'accepted' || suggestion.status === 'rejected';
  const goldAccent = '#c9a96e';

  const STATUS_CHIP = {
    accepted: { label: t('suggestion_accepted'), color: 'success' },
    rejected: { label: t('suggestion_rejected'), color: 'error' },
  };

  const diffPreview = `${truncate(suggestion.originalText, 22)} → ${truncate(suggestion.suggestedText, 22)}`;

  // ── Collapsed card ─────────────────────────────────────────────────────────
  if (!isActive) {
    return (
      <Paper
        elevation={0}
        onClick={isSettled ? undefined : onActivate}
        sx={{
          width: '100%',
          borderRadius: '12px',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          p: '8px 10px',
          boxSizing: 'border-box',
          cursor: isSettled ? 'default' : 'pointer',
          opacity: isSettled ? 0.55 : 1,
          transition: 'box-shadow 0.15s ease, border-color 0.15s ease',
          '&:hover': isSettled ? {} : {
            borderColor: 'success.light',
            boxShadow: '0 1px 6px rgba(0,0,0,0.1)',
          },
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          minHeight: 40,
        }}
      >
        <Avatar
          sx={{
            width: 22,
            height: 22,
            fontSize: '0.6rem',
            bgcolor: isSystem ? goldAccent : (suggestion.user?.color || 'primary.main'),
            flexShrink: 0,
          }}
        >
          {isSystem ? <AutoAwesomeIcon sx={{ fontSize: 14 }} /> : getInitials(suggestion.user?.first_name, suggestion.user?.last_name)}
        </Avatar>

        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              textOverflow: 'ellipsis',
              fontSize: '0.72rem',
              color: 'text.secondary',
            }}
          >
            <Box component="span" sx={{ fontWeight: 600, color: isSystem ? goldAccent : 'text.primary', mr: 0.5 }}>
              {isSystem ? 'Lawis AI' : suggestion.user?.first_name}
            </Box>
            <Box component="span" sx={{ color: 'error.main', textDecoration: 'line-through', mr: 0.3 }}>
              {truncate(suggestion.originalText, 20)}
            </Box>
            <Box component="span" sx={{ color: 'text.disabled', mx: 0.25 }}>→</Box>
            <Box component="span" sx={{ color: 'success.dark' }}>
              {truncate(suggestion.suggestedText, 20)}
            </Box>
          </Typography>
        </Box>

        {isSettled && (
          <Chip
            label={STATUS_CHIP[suggestion.status]?.label}
            color={STATUS_CHIP[suggestion.status]?.color}
            size="small"
            sx={{ fontSize: '0.65rem', height: 18, borderRadius: '4px', flexShrink: 0 }}
          />
        )}
      </Paper>
    );
  }

  // ── Expanded (active) card ─────────────────────────────────────────────────
  return (
    <Paper
      elevation={0}
      sx={{
        width: '100%',
        borderRadius: '12px',
        bgcolor: 'background.paper',
        border: '2px solid',
        borderColor: isSettled ? 'divider' : (isSystem ? goldAccent : 'success.main'),
        boxShadow: isSettled ? 'none' : '0 4px 16px rgba(0,0,0,0.12)',
        p: 1.5,
        boxSizing: 'border-box',
        opacity: isSettled ? 0.7 : 1,
      }}
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <Avatar
            sx={{
              width: 28,
              height: 28,
              fontSize: '0.7rem',
              bgcolor: isSystem ? goldAccent : (suggestion.user?.color || 'primary.main'),
              flexShrink: 0,
            }}
          >
            {isSystem ? <AutoAwesomeIcon sx={{ fontSize: 16 }} /> : getInitials(suggestion.user?.first_name, suggestion.user?.last_name)}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Typography
                variant="body2"
                sx={{ fontWeight: 700, fontSize: '0.8rem', lineHeight: 1.2, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', color: isSystem ? goldAccent : 'text.primary' }}
              >
                {isSystem ? 'Lawis AI' : `${suggestion.user?.first_name} ${suggestion.user?.last_name}`}
              </Typography>
              {isSystem && (
                <Chip label="AI" size="small" sx={{ height: 16, fontSize: '0.6rem', fontWeight: 700, bgcolor: `${goldAccent}20`, color: goldAccent, '& .MuiChip-label': { px: 0.5 } }} />
              )}
            </Box>
            <Typography variant="caption" sx={{ color: 'text.disabled', fontSize: '0.7rem' }}>
              {formatRelativeTime(suggestion.timestamp || suggestion.created_at, t)}
            </Typography>
          </Box>
        </Box>

        {isSettled ? (
          <Chip
            label={STATUS_CHIP[suggestion.status]?.label}
            color={STATUS_CHIP[suggestion.status]?.color}
            size="small"
            sx={{ fontSize: '0.68rem', height: 20, borderRadius: '4px', mt: 0.25 }}
          />
        ) : (
          <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0 }}>
            <Tooltip title={t('suggestion_accept_tooltip')} placement="top">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onAccept(); }}
                sx={{
                  width: 28,
                  height: 28,
                  color: 'success.main',
                  border: '1.5px solid',
                  borderColor: 'success.main',
                  borderRadius: '6px',
                  '&:hover': { bgcolor: 'success.50' },
                }}
              >
                <CheckIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title={t('suggestion_reject_tooltip')} placement="top">
              <IconButton
                size="small"
                onClick={(e) => { e.stopPropagation(); onReject(); }}
                sx={{
                  width: 28,
                  height: 28,
                  color: 'text.secondary',
                  border: '1.5px solid',
                  borderColor: 'divider',
                  borderRadius: '6px',
                  '&:hover': { bgcolor: 'error.50', borderColor: 'error.light', color: 'error.main' },
                }}
              >
                <CloseIcon sx={{ fontSize: 15 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </Box>

      {/* Diff display */}
      <Box
        sx={{
          borderRadius: '6px',
          overflow: 'hidden',
          border: '1px solid',
          borderColor: 'divider',
          mb: isSettled ? 0 : 1,
        }}
      >
        {/* Delete row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            px: 1,
            py: 0.6,
            bgcolor: 'error.50',
            borderBottom: '1px solid',
            borderColor: 'error.100',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'error.dark', fontWeight: 700, flexShrink: 0, lineHeight: 1.5, fontSize: '0.7rem', mt: '1px' }}
          >
            −
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'error.dark',
              textDecoration: 'line-through',
              wordBreak: 'break-word',
              lineHeight: 1.5,
              fontSize: '0.78rem',
            }}
          >
            {suggestion.originalText}
          </Typography>
        </Box>
        {/* Insert row */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 0.75,
            px: 1,
            py: 0.6,
            bgcolor: 'success.50',
          }}
        >
          <Typography
            variant="caption"
            sx={{ color: 'success.dark', fontWeight: 700, flexShrink: 0, lineHeight: 1.5, fontSize: '0.7rem', mt: '1px' }}
          >
            +
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: 'success.dark',
              wordBreak: 'break-word',
              lineHeight: 1.5,
              fontSize: '0.78rem',
            }}
          >
            {suggestion.suggestedText}
          </Typography>
        </Box>
      </Box>

      {/* Accept/Reject full-width buttons — only when active and pending */}
      {!isSettled && (
        <Box sx={{ display: 'flex', gap: 0.75 }}>
          <Button
            size="small"
            variant="contained"
            color="success"
            disableElevation
            onClick={(e) => { e.stopPropagation(); onAccept(); }}
            fullWidth
            sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', py: 0.4 }}
          >
            {t('suggestion_accept_tooltip')}
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            disableElevation
            onClick={(e) => { e.stopPropagation(); onReject(); }}
            fullWidth
            sx={{ textTransform: 'none', fontSize: '0.75rem', borderRadius: '6px', py: 0.4 }}
          >
            {t('suggestion_reject_tooltip')}
          </Button>
        </Box>
      )}
    </Paper>
  );
};

export { getInitials, formatRelativeTime };
export default SuggestionCard;
